import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Col, Form,
  Modal, Row, Spinner, Table,
} from 'react-bootstrap';
import {
  FaBell, FaCalendarAlt, FaCheck, FaEdit,
  FaMoneyBillWave, FaFileAlt, FaFileContract, FaPhone, FaPlus, FaRedo, FaSearch, FaSyncAlt,
  FaToggleOff, FaToggleOn, FaTrash, FaUser, FaWifi,
} from 'react-icons/fa';
import abonnementService from '../services/abonnementService';
import DocumentsModal from '../components/DocumentsModal';

// ── Constantes ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: 'wifi',       label: 'WiFi / Internet' },
  { value: 'ip',         label: 'Connexion IP' },
  { value: 'streaming',  label: 'Streaming' },
  { value: 'logiciel',   label: 'Logiciel / SaaS' },
  { value: 'autre',      label: 'Autre' },
];

const FREQUENCES = [
  { value: 'mensuel',      label: 'Mensuel' },
  { value: 'trimestriel',  label: 'Trimestriel' },
  { value: 'semestriel',   label: 'Semestriel' },
  { value: 'annuel',       label: 'Annuel' },
  { value: 'personnalise', label: 'Personnalisé' },
];

const CAT_COLORS = {
  wifi:      '#0d6efd',
  ip:        '#0d9488',
  streaming: '#e67e22',
  logiciel:  '#6f42c1',
  autre:     '#6c757d',
};

const CAT_LABELS = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));
const FREQ_LABELS = Object.fromEntries(FREQUENCES.map(f => [f.value, f.label]));

const TODAY = new Date().toISOString().split('T')[0];

const nbJoursRestants = (date) => {
  if (!date) return null;
  const diff = Math.ceil((new Date(date) - new Date(TODAY)) / 86400000);
  return diff;
};

const echeanceBadge = (date, alerteJours) => {
  const j = nbJoursRestants(date);
  if (j === null) return null;
  if (j < 0)  return { label: `Dépassée (${Math.abs(j)}j)`,  bg: '#dc3545', text: '#fff' };
  if (j === 0) return { label: 'Aujourd\'hui',                bg: '#dc3545', text: '#fff' };
  if (j <= alerteJours) return { label: `Dans ${j}j`,         bg: '#fd7e14', text: '#fff' };
  return { label: `Dans ${j}j`,                               bg: '#198754', text: '#fff' };
};

const MONTHLY_FACTOR = {
  mensuel: 1,
  trimestriel: 1 / 3,
  semestriel: 1 / 6,
  annuel: 1 / 12,
  personnalise: 0,
};

const MODES_PAIEMENT = [
  { value: '',            label: 'Non précisé' },
  { value: 'virement',    label: 'Virement' },
  { value: 'prelevement', label: 'Prélèvement automatique' },
  { value: 'carte',       label: 'Carte bancaire' },
  { value: 'especes',     label: 'Espèces' },
  { value: 'autre',       label: 'Autre' },
];
const MODE_PAIEMENT_LABELS = Object.fromEntries(MODES_PAIEMENT.map(m => [m.value, m.label]));

const FORM_INIT = {
  nom: '', categorie: 'wifi', fournisseur: '', montant: '',
  frequence: 'mensuel', date_debut: TODAY, alerte_jours: 7,
  actif: true, note: '',
  responsable_nom: '', responsable_telephone: '', numero_contrat: '',
  mode_paiement: '', renouvellement_auto: false,
};

// ── Composant principal ───────────────────────────────────────────────────────
export default function AbonnementsPage() {
  const [abonnements, setAbonnements]   = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  // Modals
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState(FORM_INIT);
  const [saving, setSaving]             = useState(false);

  const [showEch, setShowEch]           = useState(false);
  const [aboEch, setAboEch]             = useState(null);

  const [showDocs, setShowDocs]         = useState(false);
  const [aboDocs, setAboDocs]           = useState(null);

  const [showDelConfirm, setShowDelConfirm] = useState(false);
  const [aboToDelete, setAboToDelete]       = useState(null);
  const [deleting, setDeleting]             = useState(false);

  // Filtres
  const [filterCat, setFilterCat]       = useState('');
  const [filterActif, setFilterActif]   = useState('');
  const [search, setSearch]             = useState('');
  const [filterUrgence, setFilterUrgence] = useState(''); // '', 'critique', 'proche'

  // Toast feedback
  const [toast, setToast]               = useState(null); // { type: 'success'|'danger', message }
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Chargement ────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await abonnementService.getAll();
      setAbonnements(res.data);
    } catch {
      setError('Erreur lors du chargement des abonnements.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Filtrage ──────────────────────────────────────────────────────
  const filtered = abonnements.filter(a => {
    if (filterCat   && a.categorie !== filterCat) return false;
    if (filterActif === 'actif'    && !a.actif)   return false;
    if (filterActif === 'inactif'  && a.actif)    return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = `${a.nom || ''} ${a.fournisseur || ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (filterUrgence) {
      const j = nbJoursRestants(a.prochaine_echeance);
      if (j === null) return false;
      if (filterUrgence === 'critique' && j >= 0) return false;
      if (filterUrgence === 'proche' && (j < 0 || j > (a.alerte_jours ?? 7))) return false;
    }
    return true;
  });

  const hasActiveFilters = !!(filterCat || filterActif || search.trim() || filterUrgence);

  // Trier : en retard d'abord, puis par prochaine échéance croissante
  const sorted = [...filtered].sort((a, b) => {
    const ja = nbJoursRestants(a.prochaine_echeance);
    const jb = nbJoursRestants(b.prochaine_echeance);
    if (ja === null && jb === null) return 0;
    if (ja === null) return 1;
    if (jb === null) return -1;
    return ja - jb;
  });

  // ── Formulaire ────────────────────────────────────────────────────
  const openCreate = () => { setEditing(null); setForm(FORM_INIT); setShowForm(true); };
  const openEdit   = (abo) => {
    setEditing(abo);
    setForm({
      nom: abo.nom || '',
      categorie: abo.categorie || 'wifi',
      fournisseur: abo.fournisseur || '',
      montant: abo.montant != null ? String(abo.montant) : '',
      frequence: abo.frequence || 'mensuel',
      date_debut: abo.date_debut || TODAY,
      alerte_jours: abo.alerte_jours ?? 7,
      actif: abo.actif !== false,
      note: abo.note || '',
      responsable_nom: abo.responsable_nom || '',
      responsable_telephone: abo.responsable_telephone || '',
      numero_contrat: abo.numero_contrat || '',
      mode_paiement: abo.mode_paiement || '',
      renouvellement_auto: !!abo.renouvellement_auto,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        montant: form.montant !== '' ? parseFloat(form.montant) : null,
        alerte_jours: parseInt(form.alerte_jours, 10) || 7,
      };
      if (editing) {
        await abonnementService.update(editing.id, payload);
        showToast('success', `Abonnement "${form.nom}" modifié.`);
      } else {
        await abonnementService.create(payload);
        showToast('success', `Abonnement "${form.nom}" créé.`);
      }
      setShowForm(false);
      loadData();
    } catch {
      showToast('danger', 'Erreur lors de l\'enregistrement de l\'abonnement.');
    } finally {
      setSaving(false);
    }
  };

  // ── Suppression ───────────────────────────────────────────────────
  const confirmDelete = (abo) => { setAboToDelete(abo); setShowDelConfirm(true); };
  const handleDelete = async () => {
    if (!aboToDelete) return;
    setDeleting(true);
    try {
      await abonnementService.remove(aboToDelete.id);
      showToast('success', `Abonnement "${aboToDelete.nom}" supprimé.`);
      setShowDelConfirm(false);
      loadData();
    } catch {
      showToast('danger', 'Erreur lors de la suppression.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Activer / désactiver rapidement ────────────────────────────────
  const handleToggleActif = async (abo) => {
    try {
      await abonnementService.update(abo.id, { actif: !abo.actif });
      showToast('success', `Abonnement "${abo.nom}" ${abo.actif ? 'désactivé' : 'activé'}.`);
      loadData();
    } catch {
      showToast('danger', 'Erreur lors de la mise à jour.');
    }
  };

  // ── Marquer échéance payée ────────────────────────────────────────
  const handlePayEcheance = async (ech) => {
    const isPaid = ech.statut === 'paye';
    try {
      await abonnementService.updateEcheance(ech.id, {
        statut: isPaid ? 'en_attente' : 'paye',
        date_paiement: isPaid ? null : TODAY,
      });
      const res = await abonnementService.getOne(aboEch.id);
      setAboEch(res.data);
      loadData();
      showToast('success', isPaid ? 'Paiement annulé.' : 'Échéance marquée comme payée.');
    } catch {
      showToast('danger', 'Erreur lors de la mise à jour de l\'échéance.');
    }
  };

  // ── Rendu card abonnement ─────────────────────────────────────────
  const renderCard = (abo) => {
    const badge   = echeanceBadge(abo.prochaine_echeance, abo.alerte_jours);
    const catColor = CAT_COLORS[abo.categorie] || '#6c757d';
    const nbEch    = abo.echeances?.length || 0;
    const nbPayees = abo.echeances?.filter(e => e.statut === 'paye').length || 0;

    return (
      <Col key={abo.id} xs={12} sm={6} lg={4} xl={3} className="mb-3">
        <div style={{
          border: `2px solid ${!abo.actif ? '#dee2e6' : catColor + '55'}`,
          borderLeft: `5px solid ${!abo.actif ? '#dee2e6' : catColor}`,
          borderRadius: 10, padding: '14px 16px',
          background: !abo.actif ? '#f8f9fa' : '#fff',
          opacity: abo.actif ? 1 : 0.65,
          height: '100%', display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {/* Header */}
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#212529' }}>{abo.nom}</div>
              <div style={{ fontSize: '0.8rem', color: catColor, fontWeight: 600 }}>
                {CAT_LABELS[abo.categorie] || abo.categorie}
                {abo.fournisseur && <span style={{ color: '#6c757d', fontWeight: 400 }}> · {abo.fournisseur}</span>}
              </div>
            </div>
            {!abo.actif && (
              <span style={{ background: '#6c757d', color: '#fff', borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600 }}>
                Inactif
              </span>
            )}
          </div>

          {/* Montant + fréquence */}
          <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.88rem' }}>
            <FaMoneyBillWave style={{ color: catColor }} />
            <strong>{abo.montant != null ? Number(abo.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' DH' : '—'}</strong>
            <span style={{ color: '#6c757d' }}>/ {FREQ_LABELS[abo.frequence] || abo.frequence}</span>
          </div>

          {/* Prochaine échéance */}
          {abo.prochaine_echeance && badge && (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
              <FaCalendarAlt style={{ color: badge.bg }} />
              <span style={{ color: '#495057' }}>Échéance : {new Date(abo.prochaine_echeance).toLocaleDateString('fr-FR')}</span>
              <span style={{ background: badge.bg, color: badge.text, borderRadius: 5, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700 }}>
                {badge.label}
              </span>
            </div>
          )}

          {/* Infos complémentaires */}
          {(abo.responsable_nom || abo.responsable_telephone || abo.numero_contrat || abo.mode_paiement || abo.renouvellement_auto) && (
            <div style={{ fontSize: '0.78rem', color: '#6c757d', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {abo.responsable_nom && (
                <div><FaUser className="me-2" />{abo.responsable_nom}{abo.responsable_telephone && <span> · <FaPhone className="me-1" />{abo.responsable_telephone}</span>}</div>
              )}
              {!abo.responsable_nom && abo.responsable_telephone && (
                <div><FaPhone className="me-2" />{abo.responsable_telephone}</div>
              )}
              {abo.numero_contrat && (
                <div><FaFileContract className="me-2" />Contrat n° {abo.numero_contrat}</div>
              )}
              {(abo.mode_paiement || abo.renouvellement_auto) && (
                <div>
                  {abo.mode_paiement && <span>{MODE_PAIEMENT_LABELS[abo.mode_paiement]}</span>}
                  {abo.mode_paiement && abo.renouvellement_auto && <span> · </span>}
                  {abo.renouvellement_auto && <span><FaRedo className="me-1" />Renouvellement auto</span>}
                </div>
              )}
            </div>
          )}

          {/* Barre de progression écheances */}
          {nbEch > 0 && (
            <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
              {nbPayees}/{nbEch} échéances payées
              <div style={{ height: 4, borderRadius: 4, background: '#e9ecef', marginTop: 3 }}>
                <div style={{
                  height: '100%', borderRadius: 4,
                  background: catColor,
                  width: `${Math.round((nbPayees / nbEch) * 100)}%`,
                  transition: 'width 0.3s',
                }} />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="d-flex gap-2 mt-auto pt-1" style={{ borderTop: '1px solid #f0f0f0' }}>
            <Button size="sm" variant="outline-secondary" title="Échéances"
              onClick={() => { setAboEch(abo); setShowEch(true); }}>
              <FaBell />
            </Button>
            <Button size="sm" variant="outline-primary" title="Documents"
              onClick={() => { setAboDocs(abo); setShowDocs(true); }}>
              <FaFileAlt />
            </Button>
            <Button size="sm" variant="outline-warning" title="Modifier"
              onClick={() => openEdit(abo)}>
              <FaEdit />
            </Button>
            <Button size="sm" variant={abo.actif ? 'outline-success' : 'outline-secondary'}
              title={abo.actif ? 'Désactiver' : 'Activer'}
              onClick={() => handleToggleActif(abo)}>
              {abo.actif ? <FaToggleOn /> : <FaToggleOff />}
            </Button>
            <Button size="sm" variant="outline-danger" title="Supprimer"
              onClick={() => confirmDelete(abo)}
              className="ms-auto">
              <FaTrash />
            </Button>
          </div>
        </div>
      </Col>
    );
  };

  // ── Stats header ──────────────────────────────────────────────────
  const nbActifs   = abonnements.filter(a => a.actif).length;
  const nbAlertes  = abonnements.filter(a => {
    if (!a.actif || !a.prochaine_echeance) return false;
    const j = nbJoursRestants(a.prochaine_echeance);
    return j !== null && j <= (a.alerte_jours ?? 7);
  }).length;
  const totalMensuel = abonnements
    .filter(a => a.actif && a.montant != null)
    .reduce((sum, a) => sum + Number(a.montant) * (MONTHLY_FACTOR[a.frequence] ?? 0), 0);

  // Abonnements critiques (échéance dépassée) et proches (dans le délai d'alerte)
  const aboCritiques = abonnements
    .filter(a => a.actif && a.prochaine_echeance && nbJoursRestants(a.prochaine_echeance) < 0)
    .sort((a, b) => nbJoursRestants(a.prochaine_echeance) - nbJoursRestants(b.prochaine_echeance));
  const aboProches = abonnements
    .filter(a => {
      if (!a.actif || !a.prochaine_echeance) return false;
      const j = nbJoursRestants(a.prochaine_echeance);
      return j >= 0 && j <= (a.alerte_jours ?? 7);
    })
    .sort((a, b) => nbJoursRestants(a.prochaine_echeance) - nbJoursRestants(b.prochaine_echeance));

  const resetFilters = () => { setSearch(''); setFilterCat(''); setFilterActif(''); setFilterUrgence(''); };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="container mt-4 mb-5">
      {/* Titre */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <FaWifi size={22} style={{ color: '#0d6efd' }} />
          <h4 className="mb-0 fw-bold">Abonnements</h4>
          <Badge bg="primary" style={{ fontSize: '0.8rem' }}>{nbActifs} actifs</Badge>
          {nbAlertes > 0 && (
            <Badge bg="danger" style={{ fontSize: '0.8rem' }}>
              <FaBell className="me-1" />{nbAlertes} alerte{nbAlertes > 1 ? 's' : ''}
            </Badge>
          )}
          {totalMensuel > 0 && (
            <Badge bg="light" text="dark" style={{ fontSize: '0.8rem', border: '1px solid #dee2e6' }}>
              <FaMoneyBillWave className="me-1" />
              ~{totalMensuel.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH/mois
            </Badge>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={loadData} title="Actualiser">
            <FaSyncAlt />
          </Button>
          <Button size="sm" variant="primary" onClick={openCreate}>
            <FaPlus className="me-1" /> Nouvel abonnement
          </Button>
        </div>
      </div>

      {toast && (
        <Alert variant={toast.type} dismissible onClose={() => setToast(null)} className="py-2">
          {toast.message}
        </Alert>
      )}

      {/* Cards échéances critiques / proches */}
      <Row className="mb-3 g-2">
        <Col xs={12} md={6}>
          <div role="button"
            onClick={() => aboCritiques.length > 0 && setFilterUrgence(filterUrgence === 'critique' ? '' : 'critique')}
            style={{
              border: `2px solid ${filterUrgence === 'critique' ? '#dc3545' : (aboCritiques.length > 0 ? '#f1aeb5' : '#e9ecef')}`,
              borderRadius: 10, padding: '10px 14px',
              background: aboCritiques.length > 0 ? '#fff5f5' : '#f8f9fa',
              cursor: aboCritiques.length > 0 ? 'pointer' : 'default',
              opacity: aboCritiques.length > 0 ? 1 : 0.7,
              height: '100%',
            }}>
            <div className="d-flex align-items-center justify-content-between mb-1">
              <div className="fw-bold" style={{ color: aboCritiques.length > 0 ? '#dc3545' : '#6c757d', fontSize: '0.9rem' }}>
                <FaBell className="me-2" />Échéances dépassées ({aboCritiques.length})
              </div>
              {filterUrgence === 'critique' && <Badge bg="danger" style={{ fontSize: '0.7rem' }}>Filtré</Badge>}
            </div>
            {aboCritiques.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {aboCritiques.slice(0, 6).map(a => {
                  const j = nbJoursRestants(a.prochaine_echeance);
                  return (
                    <span key={a.id} style={{
                      background: '#dc354522', color: '#dc3545', borderRadius: 6,
                      padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600,
                    }}>
                      {a.nom} · {Math.abs(j)}j
                    </span>
                  );
                })}
                {aboCritiques.length > 6 && (
                  <span style={{ fontSize: '0.78rem', color: '#6c757d' }}>+{aboCritiques.length - 6} autre(s)</span>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '0.8rem', color: '#adb5bd' }}>Aucune échéance dépassée</div>
            )}
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div role="button"
            onClick={() => aboProches.length > 0 && setFilterUrgence(filterUrgence === 'proche' ? '' : 'proche')}
            style={{
              border: `2px solid ${filterUrgence === 'proche' ? '#fd7e14' : (aboProches.length > 0 ? '#fed8b1' : '#e9ecef')}`,
              borderRadius: 10, padding: '10px 14px',
              background: aboProches.length > 0 ? '#fff8f0' : '#f8f9fa',
              cursor: aboProches.length > 0 ? 'pointer' : 'default',
              opacity: aboProches.length > 0 ? 1 : 0.7,
              height: '100%',
            }}>
            <div className="d-flex align-items-center justify-content-between mb-1">
              <div className="fw-bold" style={{ color: aboProches.length > 0 ? '#fd7e14' : '#6c757d', fontSize: '0.9rem' }}>
                <FaCalendarAlt className="me-2" />Échéances proches ({aboProches.length})
              </div>
              {filterUrgence === 'proche' && <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>Filtré</Badge>}
            </div>
            {aboProches.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {aboProches.slice(0, 6).map(a => {
                  const j = nbJoursRestants(a.prochaine_echeance);
                  return (
                    <span key={a.id} style={{
                      background: '#fd7e1422', color: '#c2540a', borderRadius: 6,
                      padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600,
                    }}>
                      {a.nom} · {j === 0 ? "aujourd'hui" : `dans ${j}j`}
                    </span>
                  );
                })}
                {aboProches.length > 6 && (
                  <span style={{ fontSize: '0.78rem', color: '#6c757d' }}>+{aboProches.length - 6} autre(s)</span>
                )}
              </div>
            ) : (
              <div style={{ fontSize: '0.8rem', color: '#adb5bd' }}>Aucune échéance proche</div>
            )}
          </div>
        </Col>
      </Row>

      {/* Filtres */}
      <Row className="mb-3 g-2">
        <Col xs={12} sm={4} md={4}>
          <div className="position-relative">
            <FaSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#adb5bd', fontSize: '0.85rem' }} />
            <Form.Control size="sm" placeholder="Rechercher par nom ou fournisseur..."
              style={{ paddingLeft: 30 }}
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </Col>
        <Col xs={12} sm={4} md={3}>
          <Form.Select size="sm" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Form.Select>
        </Col>
        <Col xs={12} sm={4} md={3}>
          <Form.Select size="sm" value={filterActif} onChange={e => setFilterActif(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="actif">Actifs seulement</option>
            <option value="inactif">Inactifs seulement</option>
          </Form.Select>
        </Col>
        {hasActiveFilters && (
          <Col xs={12} sm={12} md={2} className="d-flex align-items-center">
            <Button size="sm" variant="link" className="text-decoration-none px-0"
              onClick={resetFilters}>
              Réinitialiser les filtres
            </Button>
          </Col>
        )}
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : sorted.length === 0 ? (
        <div className="text-center text-muted py-5">
          <FaWifi size={40} style={{ opacity: 0.2 }} />
          {hasActiveFilters ? (
            <>
              <div className="mt-2">Aucun abonnement ne correspond à ces filtres</div>
              <Button variant="outline-secondary" size="sm" className="mt-3"
                onClick={resetFilters}>
                Réinitialiser les filtres
              </Button>
            </>
          ) : (
            <>
              <div className="mt-2">Aucun abonnement trouvé</div>
              <Button variant="primary" size="sm" className="mt-3" onClick={openCreate}>
                <FaPlus className="me-1" /> Créer le premier abonnement
              </Button>
            </>
          )}
        </div>
      ) : (
        <Row>{sorted.map(renderCard)}</Row>
      )}

      {/* ── Modal Formulaire ────────────────────────────────────────── */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#f8f9fa' }}>
          <Modal.Title>{editing ? 'Modifier l\'abonnement' : 'Nouvel abonnement'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Nom *</Form.Label>
                <Form.Control size="sm" placeholder="ex: Netflix, Fibre Orange..."
                  value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Catégorie</Form.Label>
                <Form.Select size="sm" value={form.categorie}
                  onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Fournisseur</Form.Label>
                <Form.Control size="sm" placeholder="Orange, Netflix Inc., Microsoft..."
                  value={form.fournisseur} onChange={e => setForm(f => ({ ...f, fournisseur: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold">Montant (DH)</Form.Label>
                <Form.Control size="sm" type="number" min="0" step="0.01" placeholder="0.00"
                  value={form.montant} onChange={e => setForm(f => ({ ...f, montant: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label className="fw-semibold">Fréquence</Form.Label>
                <Form.Select size="sm" value={form.frequence}
                  onChange={e => setForm(f => ({ ...f, frequence: e.target.value }))}>
                  {FREQUENCES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={6} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Date de début</Form.Label>
                <Form.Control size="sm" type="date" value={form.date_debut}
                  onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={6} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Alerte (jours avant)</Form.Label>
                <Form.Control size="sm" type="number" min="1" max="90" value={form.alerte_jours}
                  onChange={e => setForm(f => ({ ...f, alerte_jours: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="d-flex align-items-end">
              <Form.Check type="switch" label="Abonnement actif"
                checked={form.actif}
                onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))} />
            </Col>
            <Col xs={12}><hr className="my-1" /></Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Responsable du suivi</Form.Label>
                <Form.Control size="sm" placeholder="Nom du responsable"
                  value={form.responsable_nom} onChange={e => setForm(f => ({ ...f, responsable_nom: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Téléphone du responsable</Form.Label>
                <Form.Control size="sm" placeholder="06 12 34 56 78"
                  value={form.responsable_telephone} onChange={e => setForm(f => ({ ...f, responsable_telephone: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">N° de contrat</Form.Label>
                <Form.Control size="sm" placeholder="Référence contrat"
                  value={form.numero_contrat} onChange={e => setForm(f => ({ ...f, numero_contrat: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">Mode de paiement</Form.Label>
                <Form.Select size="sm" value={form.mode_paiement}
                  onChange={e => setForm(f => ({ ...f, mode_paiement: e.target.value }))}>
                  {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={4} className="d-flex align-items-end">
              <Form.Check type="switch" label="Renouvellement automatique"
                checked={form.renouvellement_auto}
                onChange={e => setForm(f => ({ ...f, renouvellement_auto: e.target.checked }))} />
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="fw-semibold">Note</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" placeholder="Informations complémentaires..."
                  value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowForm(false)}>Annuler</Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !form.nom.trim()}>
            {saving ? <Spinner size="sm" animation="border" className="me-1" /> : null}
            {editing ? 'Enregistrer' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Échéances ──────────────────────────────────────────── */}
      <Modal show={showEch} onHide={() => setShowEch(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: '#f8f9fa' }}>
          <Modal.Title>
            <FaBell className="me-2 text-warning" />
            Échéances — {aboEch?.nom}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {!aboEch?.echeances?.length ? (
            <div className="text-muted text-center py-3">Aucune échéance générée</div>
          ) : (
            <Table size="sm" hover className="mb-0">
              <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                <tr>
                  <th>Date échéance</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th>Date paiement</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {aboEch.echeances.map(ech => {
                  const j = nbJoursRestants(ech.date_echeance);
                  const isPaid = ech.statut === 'paye';
                  const isLate = ech.statut === 'en_retard' || (!isPaid && j !== null && j < 0);
                  return (
                    <tr key={ech.id} style={{ background: isPaid ? '#f0fdf4' : isLate ? '#fff5f5' : undefined }}>
                      <td style={{ fontWeight: 600 }}>
                        {new Date(ech.date_echeance).toLocaleDateString('fr-FR')}
                      </td>
                      <td>{ech.montant != null ? Number(ech.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' DH' : '—'}</td>
                      <td>
                        {isPaid
                          ? <span className="badge bg-success">Payée</span>
                          : isLate
                          ? <span className="badge bg-danger">En retard</span>
                          : j !== null && j <= (aboEch?.alerte_jours ?? 7)
                          ? <span className="badge bg-warning text-dark">Dans {j}j</span>
                          : <span className="badge bg-secondary">En attente</span>
                        }
                      </td>
                      <td style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                        {ech.date_paiement ? new Date(ech.date_paiement).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="text-center">
                        <Button size="sm"
                          variant={isPaid ? 'outline-secondary' : 'outline-success'}
                          title={isPaid ? 'Annuler paiement' : 'Marquer payée'}
                          onClick={() => handlePayEcheance(ech)}
                          style={{ padding: '2px 8px' }}>
                          <FaCheck />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowEch(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Suppression ────────────────────────────────────────── */}
      <Modal show={showDelConfirm} onHide={() => setShowDelConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem' }}>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Supprimer <strong>{aboToDelete?.nom}</strong> et toutes ses échéances ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDelConfirm(false)}>Annuler</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" animation="border" className="me-1" /> : null}
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Documents ──────────────────────────────────────────── */}
      {aboDocs && (
        <DocumentsModal
          show={showDocs}
          onHide={() => setShowDocs(false)}
          module="abonnement"
          moduleId={aboDocs.id}
          itemLabel={aboDocs.nom}
          canDelete={true}
        />
      )}
    </div>
  );
}
